import React from 'react';
import snakeCase from 'lodash/snakeCase';
import { useQueryProvider } from '@/modules/QueryProvider';
import DataListTable, { type DataListTableProps } from './DataListTable';

export type DataListProps<DataType extends object> = Omit<
  DataListTableProps<DataType>,
  | 'onPageChange'
  | 'onPageSizeChange'
  | 'onSortClick'
  | 'page'
  | 'pageSize'
  | 'sortBy'
  | 'sortOrder'
>;

export default function DataList<DataType extends object>({
  columns,
  data,
  isLagging,
  rowKey,
  tableHeader,
  totalRows,
  hidePageSize = false,
  ...tableProps
}: DataListProps<DataType>) {
  const { pagination, sorting, sortableFields, updateQuery } =
    // TODO(jon): We should be able to do useQueryProvider<DataType> here, but
    // that opens up other issues and requires us to update existing DataLists.
    useQueryProvider();

  const handlePageChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, value: number) =>
      updateQuery({ page: value }),
    [updateQuery],
  );

  const handleSortClick = React.useCallback(
    (sortBy: Extract<keyof DataType, string>) =>
      updateQuery({ sortBy: snakeCase(sortBy) }),
    [updateQuery],
  );

  const handlePageSizeChange = React.useCallback(
    (e: React.ChangeEvent<{ value: string }>) =>
      updateQuery({ pageSize: e.target.value }),
    [updateQuery],
  );

  return (
    <DataListTable
      columns={columns}
      data={data}
      isLagging={isLagging}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onSortClick={handleSortClick}
      page={pagination.page as number}
      pageSize={pagination.pageSize as number}
      rowKey={rowKey}
      sortBy={sorting.sortBy as Extract<keyof DataType, string>} // A little bit of a hack. See above TODO for how we should be able to address this.
      sortOrder={sorting.sortOrder}
      sortableFields={sortableFields}
      tableHeader={tableHeader}
      totalRows={totalRows}
      hidePageSize={hidePageSize}
      {...tableProps}
    />
  );
}
